import React, { useState, useEffect } from 'react';
import { FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { __ } from './translations';
import SearchInput from './components/SearchInput';
import EditButton from './components/EditButton';
import YearTabs from './components/YearTabs';
import TagList from './components/TagList';

const KnowledgeBase = () => {
    const [articles, setArticles] = useState([]);
    const [filteredArticles, setFilteredArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeYear, setActiveYear] = useState('all');
    const [availableYears, setAvailableYears] = useState([]);
    const [activeTag, setActiveTag] = useState('');

    useEffect(() => {
        const fetchArticles = async () => {
            try {
                const response = await fetch(
                    `${boardMemberData.restURL}board-member/v1/member-information/knowledge-base`,
                    {
                        headers: {
                            'X-WP-Nonce': boardMemberData.nonce
                        }
                    }
                );
                
                if (!response.ok) throw new Error('Network response was not ok');
                const data = await response.json();
                
                // Extract unique publication years
                const years = [...new Set(
                    data
                        .filter(item => item.publication_year) // Filter out items with no publication year
                        .map(item => item.publication_year)
                )];
                
                setAvailableYears(years);
                setArticles(data);
                setFilteredArticles(data);
            } catch (err) {
                setError('Failed to load knowledge base articles');
                console.error('Error fetching articles:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchArticles();
    }, []);

    // Apply filters: year, tag, and search term
    useEffect(() => {
        let filtered = articles;
        
        // Apply year filter first
        if (activeYear !== 'all') {
            filtered = filtered.filter(article => article.publication_year === activeYear);
        }
        
        // Apply tag filter
        if (activeTag) {
            filtered = filtered.filter(article => 
                article.tags && article.tags.some(tag => 
                    tag.name.toLowerCase() === activeTag.toLowerCase()
                )
            );
        }
        
        // Then apply search filter
        if (searchTerm) {
            filtered = filtered.filter(article => {
                const tagString = article.tags ? article.tags.map(tag => tag.name).join(' ') : '';
                const searchString = `${article.title} ${article.content} ${tagString}`.toLowerCase();
                return searchString.includes(searchTerm.toLowerCase());
            });
        }
        
        setFilteredArticles(filtered);
    }, [activeYear, activeTag, searchTerm, articles]);

    const toggleArticle = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };
    
    const handleSearch = (term) => {
        setSearchTerm(term);
    };
    
    const handleTagClick = (tagName) => {
        setActiveTag(tagName === activeTag ? '' : tagName);
    };
    
    // Reset all filters
    const resetFilters = () => {
        setActiveYear('all');
        setActiveTag('');
        setSearchTerm('');
    };
    
    if (loading) {
        return <div className="text-center py-6">{__('Loading...')}</div>;
    }

    if (error) {
        return <div className="text-red-500 py-6">{error}</div>;
    }

    if (!articles.length) {
        return <div className="text-center py-6">{__('No knowledge base articles available.')}</div>;
    }

    // Check if filters are active
    const hasActiveFilters = activeYear !== 'all' || activeTag !== '' || searchTerm !== '';

    return (
        <>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">{__('Knowledge Base')}</h2>
                <div className="w-1/4">
                    <SearchInput 
                        onSearch={handleSearch} 
                        placeholder={__('Search knowledge base...')}
                        value={searchTerm}
                    />
                </div>
            </div>
            
            {availableYears.length > 0 && (
                <YearTabs
                    years={availableYears}
                    activeYear={activeYear}
                    setActiveYear={setActiveYear}
                />
            )}
            
            {/* Active tag display */}
            {activeTag && (
                <div className="mb-4 flex items-center">
                    <span className="mr-2">{__('Active tag:')}</span>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {activeTag}
                    </span>
                    <button 
                        onClick={() => setActiveTag('')}
                        className="ml-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                        {__('Clear')}
                    </button>
                </div>
            )}
            
            {/* Reset filters button - show only when filters are active */}
            {hasActiveFilters && (
                <div className="mb-4">
                    <button 
                        onClick={resetFilters}
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                        {__('Reset all filters')}
                    </button>
                </div>
            )}
            
            {filteredArticles.length === 0 ? (
                <div className="text-center py-6">{__('No articles match your filters.')}</div>
            ) : (
                <div className="space-y-4">
                    {filteredArticles.map((article) => (
                        <div 
                            key={article.id} 
                            className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden"
                        >
                            <div className="flex items-center px-6 py-4 bg-gray-50">
                                <button
                                    onClick={() => toggleArticle(article.id)}
                                    className="flex-grow flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                                >
                                    <div>
                                        <h3 
                                            className="text-lg font-semibold" 
                                            dangerouslySetInnerHTML={{ __html: article.title }}
                                        />
                                        {article.publication_year && (
                                            <span className="ml-2 px-2 py-1 text-xs bg-gray-200 rounded-full">
                                                {article.publication_year}
                                            </span>
                                        )}
                                    </div>
                                    {expandedId === article.id ? (
                                        <ChevronUp className="w-5 h-5 text-gray-500" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5 text-gray-500" />
                                    )}
                                </button>
                                
                                <div className="ml-4">
                                    <EditButton postId={article.id} />
                                </div>
                            </div>

                            {expandedId === article.id && (
                                <div className="p-6">
                                    <div className="text-sm text-gray-500 mb-4">
                                        {__('Posted on')} {new Date(article.date).toLocaleDateString()}
                                    </div>
                                    
                                    {/* Display tags when article is expanded */}
                                    {article.tags && article.tags.length > 0 && (
                                        <TagList 
                                            tags={article.tags} 
                                            onTagClick={handleTagClick}
                                            className="mb-3" 
                                        />
                                    )}
                                    
                                    <div 
                                        className="prose max-w-none mb-4"
                                        dangerouslySetInnerHTML={{ __html: article.content }}
                                    />

                                    {article.pdf_url && (
                                        <div className="pt-4 border-t">
                                            <a
                                                href={article.pdf_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                            >
                                                <FileText className="w-4 h-4 mr-2" />
                                                {__('Download PDF')}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default KnowledgeBase;